import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { type Commande, type StatutCommande, statutLabels, statutColors } from '../../mocks/commandes';
import { AssignmentModal } from '../commande-detail/components/AssignmentModal';

interface UserInfo { id: string; nom: string; role: string; }

const getArticleNomHelper = (aff: any, cmd: any) => {
  let baseName = cmd.articles_summary || cmd.modele || 'Toute la commande';
  if (aff.article_nom) baseName = aff.article_nom;
  if (aff.article_id && cmd.articles) {
    const art = cmd.articles.find((a: any) => Number(a.id) === Number(aff.article_id));
    if (art) baseName = `${art.type_habit_nom || 'Article'} (${art.modele_nom || 'Sur mesure'})`;
  }
  return baseName;
};

const getGroupedAffectations = (affectations: any[], activeTab: string) => {
  const filtered = (affectations || []).filter((a: any) => a.type === activeTab);
  const groups: Record<string, { 
    personnel_id: number;
    personnel_nom: string;
    total_qty: number;
    completed_qty: number;
    active_affs: any[];
  }> = {};
  
  filtered.forEach((aff: any) => {
    const pId = String(aff.personnel_id);
    if (!groups[pId]) {
      groups[pId] = {
        personnel_id: Number(aff.personnel_id),
        personnel_nom: aff.personnel_nom || 'Inconnu',
        total_qty: 0,
        completed_qty: 0,
        active_affs: []
      };
    }
    const qty = Number(aff.quantite || 1);
    groups[pId].total_qty += qty;
    if (aff.statut === 'TERMINE') {
      groups[pId].completed_qty += qty;
    } else {
      groups[pId].active_affs.push(aff);
    }
  });
  return Object.values(groups);
};



const renderDetailedSubTasks = (cmd: any, type: string) => {
  const affs = (cmd.affectations || []).filter((a: any) => a.type === type);
  if (affs.length === 0) return null;

  const groups: Record<string, { tache: string; articleNom: string; finished: number; total: number; personnels: string[] }> = {};
  affs.forEach((aff: any) => {
    const taskText = aff.tache || 'Tâche';
    const key = `${aff.article_id || 'global'}_${taskText}`;
    if (!groups[key]) {
      groups[key] = {
        tache: taskText,
        articleNom: aff.article_name || aff.article_nom ? ` (${aff.article_name || aff.article_nom})` : '',
        finished: 0,
        total: 0,
        personnels: []
      };
    }
    if (aff.statut === 'TERMINE') {
      groups[key].finished += Number(aff.quantite || 1);
    }
    groups[key].total += Number(aff.quantite || 1);
    if (aff.personnel_nom && !groups[key].personnels.includes(aff.personnel_nom)) {
      groups[key].personnels.push(aff.personnel_nom);
    }
  });

  const getIcon = (tache: string) => {
    if (!tache) return '📋';
    const lower = tache.toLowerCase();
    if (lower.includes('haut') || lower.includes('habit') || lower.includes('robe') || lower.includes('chemise') || lower.includes('boubou') || lower.includes('veste')) return '👗';
    if (lower.includes('pantalon') || lower.includes('bas') || lower.includes('culotte') || lower.includes('jupe')) return '👖';
    if (lower.includes('coupe')) return '✂️';
    if (lower.includes('couture') || lower.includes('complet')) return '🧵';
    if (lower.includes('broderie') || lower.includes('motif')) return '✨';
    return '📋';
  };

  return (
    <div className="mt-2 space-y-1.5 pl-1 w-full text-left">
      {Object.values(groups).map((g, idx) => (
        <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between text-[10px] bg-gray-50 border border-gray-100 rounded-lg px-2 py-0.5 gap-1 w-full">
          <span className="font-bold text-gray-700 flex items-center gap-1">
            <span>{getIcon(g.tache)}</span>
            <span>{g.tache}{g.articleNom}</span>
          </span>
          <div className="flex items-center gap-1 font-semibold text-gray-500 justify-between sm:justify-end">
            <span className={g.finished === g.total ? 'text-green-600 font-bold' : 'text-gray-700'}>
              {g.finished} / {g.total}
            </span>
            <span className="text-[9px] text-gray-400 italic pl-1">
              (par {g.personnels.join(', ')})
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const renderProgressBlock = (cmd: any, activeTab: 'COUPE' | 'COUTURE' | 'BRODERIE') => {
  const total = Number((cmd as any).total_quantite || 1);
  const cut = Number((cmd as any).coupe_terminee_quantite || 0);
  const sewn = Number((cmd as any).couture_terminee_quantite || 0);
  const brod = Number((cmd as any).broderie_terminee_quantite || 0);

  let finished = 0;
  let label = '';
  let color = 'bg-blue-500';
  let extraText = '';

  if (activeTab === 'COUPE') {
    finished = cut;
    label = finished > 1 ? 'coupés' : 'coupé';
    color = 'bg-blue-500';
  } else if (activeTab === 'COUTURE') {
    finished = sewn;
    label = finished > 1 ? 'cousus' : 'cousu';
    color = 'bg-purple-500';
    if (cut < total) {
      extraText = ` (${cut} coupés, ${total - cut} en attente de coupe)`;
    }
  } else if (activeTab === 'BRODERIE') {
    finished = brod;
    label = finished > 1 ? 'brodés' : 'brodé';
    color = 'bg-amber-500';
    if (cut < total) {
      extraText = ` (${cut} coupés, ${total - cut} en attente de coupe)`;
    }
  }

  const pct = Math.min(100, (finished / total) * 100);

  return (
    <div className="mt-1.5 flex flex-col gap-1 w-full text-left">
      <div className="flex items-center gap-2">
        <div className="w-20 bg-gray-150 rounded-full h-1.5 overflow-hidden flex-shrink-0">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${color}`} 
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[10px] font-black text-gray-500 leading-none">
          {activeTab === 'COUPE' ? (
            `${finished} / ${total} ${label}`
          ) : (
            `${finished} / ${cut} ${label}${extraText}`
          )}
        </span>
      </div>
    </div>
  );
};


export default function ProductionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandes, setCommandes] = useState<Commande[]>([]);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const [tabsOrder, setTabsOrder] = useState<('COUPE' | 'BRODERIE' | 'COUTURE')[]>(() => {
    const saved = localStorage.getItem('atelier_bassoum_prod_tabs');
    return saved ? JSON.parse(saved) : ['COUPE', 'BRODERIE', 'COUTURE'];
  });
  const [activeTab, setActiveTab] = useState<'COUPE' | 'COUTURE' | 'BRODERIE'>('COUPE');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState<any | null>(null);
  const [showValidModal, setShowValidModal] = useState(false);
  const [validComment, setValidComment] = useState('');
  const [showUndoModal, setShowUndoModal] = useState<{id: string, code: string} | null>(null);
  const [selectedAffIds, setSelectedAffIds] = useState<number[]>([]);
  const [completedQuantities, setCompletedQuantities] = useState<Record<number, number>>({});
  const [completedArticles, setCompletedArticles] = useState<Record<number, number | null>>({});

  useEffect(() => {
    if (showValidModal) {
      const activeAffs = commandes
        .filter(c => selectedIds.includes(Number(c.id)))
        .flatMap(c => ((c as any).affectations || []).filter((a: any) => a.type === activeTab && a.statut !== 'TERMINE'));
      setSelectedAffIds(activeAffs.map((a: any) => Number(a.id)));
      setCompletedQuantities({});
      setCompletedArticles({});
    } else {
      setSelectedAffIds([]);
      setCompletedQuantities({});
      setCompletedArticles({});
    }
  }, [showValidModal, selectedIds, commandes, activeTab]);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    const token = localStorage.getItem('atelier_bassoum_token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      fetchData(token);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    localStorage.setItem('atelier_bassoum_prod_tabs', JSON.stringify(tabsOrder));
  }, [tabsOrder]);

  const fetchData = async (token: string) => {
    try {
      const [cmdData, personnelData] = await Promise.all([
        api.getCommandes(token),
        api.getPersonnel(token)
      ]);
      setCommandes(cmdData);
      setPersonnel(personnelData);
    } catch (err) {
      console.error(err);
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredCommandes = commandes.filter(c => {
    if (c.statut === 'LIVRE') return false;

    const totalQty = Number((c as any).total_quantite || 1);
    const coupeQty = Number((c as any).coupe_terminee_quantite || 0);
    const coutureQty = Number((c as any).couture_terminee_quantite || 0);
    const broderieQty = Number((c as any).broderie_terminee_quantite || 0);

    const getCompletedQty = (type: 'COUPE' | 'COUTURE' | 'BRODERIE') => {
      if (type === 'COUPE') return coupeQty;
      if (type === 'COUTURE') return coutureQty;
      if (type === 'BRODERIE') return broderieQty;
      return 0;
    };

    // 1. Si l'atelier courant a déjà traité toutes les pièces, on n'affiche plus la commande dans cet atelier
    const currentCompleted = getCompletedQty(activeTab);
    if (currentCompleted >= totalQty) {
      return false;
    }

    // 2. Si ce n'est pas le premier atelier de la séquence, on vérifie si l'atelier précédent a commencé à valider des pièces
    const idx = tabsOrder.indexOf(activeTab);
    if (idx > 0) {
      const prevWorkshop = tabsOrder[idx - 1];
      const prevCompleted = getCompletedQty(prevWorkshop);
      if (prevCompleted === 0) {
        return false; // L'atelier précédent n'a encore rien validé, on ne peut pas commencer
      }
    }

    // 3. Filtrage par statut global pour éviter les incohérences
    if (activeTab === 'COUPE') {
      return ['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE'].includes(c.statut);
    }
    return ['EN_COUPE', 'EN_COUTURE', 'TERMINE'].includes(c.statut);
  });

  const [searchQuery, setSearchQuery] = useState('');

  const searchedCommandes = useMemo(() => {
    if (!searchQuery.trim()) return filteredCommandes;
    const query = searchQuery.toLowerCase().trim();
    return filteredCommandes.filter(c => {
      const matchId = String(c.id).includes(query);
      const matchClient = c.client_nom?.toLowerCase().includes(query);
      const matchModel = c.modele?.toLowerCase().includes(query);
      
      const primaryAssignee = activeTab === 'COUPE' ? c.coupeur : c.tailleur;
      const matchPrimaryAssignee = primaryAssignee?.toLowerCase().includes(query);
      
      const matchGranularAssignees = (c as any).affectations?.some((a: any) => 
        a.type === activeTab && a.personnel_nom?.toLowerCase().includes(query)
      );

      return matchId || matchClient || matchModel || matchPrimaryAssignee || matchGranularAssignees;
    });
  }, [filteredCommandes, searchQuery, activeTab]);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === searchedCommandes.length) setSelectedIds([]);
    else setSelectedIds(searchedCommandes.map(c => Number(c.id)));
  };



  const handleBatchValidate = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      setLoading(true);

      // 1. Valider d'abord les tâches sélectionnées
      for (const affId of selectedAffIds) {
        const qtyDone = completedQuantities[affId]; // Peut être undefined
        const articleId = completedArticles[affId]; // Peut être undefined
        await api.updateAffectationStatus(token, affId, 'TERMINE', qtyDone, articleId);
      }

      // 2. Valider ensuite l'étape globale des commandes
      let globalCount = 0;
      for (const id of selectedIds) {
        const cmd = commandes.find(c => Number(c.id) === id);
        if (!cmd) continue;
        
        const cmdAffs = ((cmd as any).affectations || []).filter((a: any) => a.type === activeTab);
        const isStepFullyCompleted = cmdAffs.every((aff: any) => {
          const isCompleted = aff.statut === 'TERMINE';
          const isChecked = selectedAffIds.includes(Number(aff.id));
          if (isCompleted) return true;
          if (!isChecked) return false;
          
          const valQty = completedQuantities[Number(aff.id)] ?? Number(aff.quantite || 1);
          return valQty === Number(aff.quantite || 1);
        });

        if (isStepFullyCompleted) {
          const currentStatut = cmd.statut;
          const currentTabIndex = tabsOrder.indexOf(activeTab);
          let nextStatut: StatutCommande | null = null;

          if (currentTabIndex !== -1) {
            const isLastWorkshop = (currentTabIndex === tabsOrder.length - 1);
            if (isLastWorkshop) {
              // Si c'est le dernier atelier de la séquence active, la commande passe à 'TERMINE'
              if (!['TERMINE', 'LIVRE'].includes(currentStatut)) {
                nextStatut = 'TERMINE';
              }
            } else {
              // Sinon, on transitionne vers le statut global de l'atelier suivant
              const nextWorkshop = tabsOrder[currentTabIndex + 1];
              if (nextWorkshop === 'COUPE') {
                if (!['EN_COUPE', 'EN_COUTURE', 'TERMINE', 'LIVRE'].includes(currentStatut)) {
                  nextStatut = 'EN_COUPE';
                }
              } else {
                // Pour COUTURE ou BRODERIE, le statut global est 'EN_COUTURE'
                if (!['EN_COUTURE', 'TERMINE', 'LIVRE'].includes(currentStatut)) {
                  nextStatut = 'EN_COUTURE';
                }
              }
            }
          }

          if (nextStatut) {
            await api.updateCommandeStatus(token, String(id), nextStatut, validComment);
            globalCount++;
          }
        }
      }
      showToast(globalCount > 0 ? `Validation effectuée. ${globalCount} commande(s) passée(s) à l'étape suivante.` : 'Tâches enregistrées avec succès.');
      setSelectedIds([]);
      setValidComment('');
      setCompletedQuantities({});
      setShowValidModal(false);
      fetchData(token);
    } catch (err) {
      showToast('Erreur lors de la validation groupée', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = async (id: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      setLoading(true);
      await api.revertCommandeStatus(token, id);
      showToast('Dernière étape annulée');
      setShowUndoModal(null);
      fetchData(token);
    } catch (err) {
      showToast("Erreur lors de l'annulation", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAffectation = async (affId: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    if (!window.confirm("Voulez-vous vraiment retirer ce personnel de cette tâche ?")) return;
    try {
      setLoading(true);
      await api.deleteAffectation(token, affId);
      showToast("Affectation retirée.");
      fetchData(token);
    } catch (err: any) {
      showToast(err.message || "Erreur lors de la suppression", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (affs: any[]) => {
    if (!affs || affs.length === 0) return;
    if (!window.confirm("Voulez-vous vraiment retirer ce personnel de cette tâche ?")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      setLoading(true);
      for (const a of affs) {
        await api.deleteAffectation(token, a.id);
      }
      showToast("Affectation retirée.");
      fetchData(token);
    } catch (err: any) {
      showToast(err.message || "Erreur lors de la suppression", 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!user || loading && commandes.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foret-600"></div>
      </div>
    );
  }

  const staff = personnel.filter(p => {
    if (activeTab === 'COUPE') return p.specialite === 'coupeur' || p.specialite === 'Coupeur';
    if (activeTab === 'BRODERIE') return p.specialite === 'brodeur' || p.specialite === 'Brodeur';
    return p.specialite === 'tailleur' || p.specialite === 'Tailleur';
  });

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Sidebar - Fixe sur PC, masquée sur mobile (car gérée par App.tsx) */}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 md:px-8 py-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => (window as any).toggleMobileMenu?.()}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-600 active:scale-95 transition-all"
                title="Menu mobile"
              >
                <i className="ri-menu-2-line text-xl" />
              </button>
              <div>
                <h1 className="font-playfair font-bold text-lg md:text-2xl text-gray-900 leading-tight">Gestion de Production</h1>
                <p className="text-[10px] md:text-sm text-gray-400 mt-1">Assignation groupée</p>
              </div>
            </div>
            <div className="flex bg-gray-100 p-1 rounded-xl items-center relative">
              {tabsOrder.map((t, idx) => {
                const label = t === 'COUPE' ? 'Atelier Coupe' : t === 'BRODERIE' ? 'Atelier Broderie' : 'Atelier Couture';
                const icon = t === 'COUPE' ? 'ri-scissors-line' : t === 'BRODERIE' ? 'ri-brush-line' : 'ri-sewing-thread-line';
                const color = t === 'COUPE' ? 'text-foret-700' : t === 'BRODERIE' ? 'text-amber-700' : 'text-purple-700';
                
                return (
                  <div key={t} className="flex items-center group relative">
                    <button 
                      onClick={() => { setActiveTab(t); setSelectedIds([]); }}
                      className={`px-6 py-2.5 rounded-lg text-sm font-black transition-all flex items-center gap-2 ${activeTab === t ? `bg-white ${color} shadow-sm` : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <i className={icon} />
                      <span className="hidden sm:inline">{label}</span>
                    </button>
                    
                    {/* Tiny controller to move left/right (visible on hover) */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-gray-100 rounded-md shadow-sm p-0.5 z-20">
                      {idx > 0 && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); const next = [...tabsOrder]; [next[idx], next[idx-1]] = [next[idx-1], next[idx]]; setTabsOrder(next); }}
                          title="Déplacer à gauche"
                          className="w-4 h-4 flex items-center justify-center text-[10px] text-gray-400 hover:text-foret-500 hover:bg-gray-50 rounded"
                        >
                          <i className="ri-arrow-left-s-line" />
                        </button>
                      )}
                      {idx < tabsOrder.length - 1 && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); const next = [...tabsOrder]; [next[idx], next[idx+1]] = [next[idx+1], next[idx]]; setTabsOrder(next); }}
                          title="Déplacer à droite"
                          className="w-4 h-4 flex items-center justify-center text-[10px] text-gray-400 hover:text-foret-500 hover:bg-gray-50 rounded"
                        >
                          <i className="ri-arrow-right-s-line" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Search bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2 border-t border-gray-100 pt-4">
            <div className="relative max-w-md w-full">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold" />
              <input
                type="text"
                placeholder="Rechercher par N°, client, modèle, personnel..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-gray-800 placeholder-gray-400 focus:bg-white focus:border-foret-500 focus:ring-0 transition-all outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Effacer la recherche"
                >
                  <i className="ri-close-circle-fill text-lg" />
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs font-bold text-gray-400">
                {searchedCommandes.length} résultat{searchedCommandes.length > 1 ? 's' : ''} trouvé{searchedCommandes.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 relative">
          {/* Mobile view: Items list (Cards) */}
          <div className="md:hidden space-y-3 pb-24">
            {searchedCommandes.map(cmd => (
              <div key={cmd.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      aria-label={`Sélectionner commande ${cmd.id}`}
                      className="w-5 h-5 rounded border-gray-300 text-foret-600 focus:ring-foret-500 cursor-pointer"
                      checked={selectedIds.includes(Number(cmd.id))}
                      onChange={() => toggleSelect(Number(cmd.id))}
                    />
                    <span className="font-black text-sm text-gray-900">#{cmd.id}</span>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${statutColors[cmd.statut]}`}>
                    {statutLabels[cmd.statut]}
                  </span>
                </div>
                
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-50 flex-shrink-0 overflow-hidden border border-gray-100">
                    <img src={api.getProxyUrl(cmd.modelImage)} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black text-gray-900 truncate uppercase tracking-tight">{cmd.client_nom}</p>
                    <p className="text-xs text-gray-500 truncate">{cmd.modele || 'Sur mesure'}</p>
                    {renderProgressBlock(cmd, activeTab)}

                    {/* Granular affectations list for mobile view */}
                    {(cmd as any).affectations && (cmd as any).affectations.filter((a: any) => a.type === activeTab).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {getGroupedAffectations((cmd as any).affectations, activeTab).map((g: any) => {
                          const isFullyCompleted = g.completed_qty === g.total_qty;
                          return (
                            <span 
                              key={g.personnel_id}
                              className={`text-[8px] font-black px-2 py-0.5 rounded-lg border shadow-sm flex items-center gap-1 ${
                                isFullyCompleted 
                                  ? 'bg-green-50 text-green-700 border-green-200' 
                                  : 'bg-blue-50 text-blue-700 border-blue-200'
                              }`}
                              title={`${g.personnel_nom} : ${g.completed_qty} / ${g.total_qty} terminés`}
                            >
                              <span>{g.personnel_nom === 'Inconnu' ? `ID:${g.personnel_id}` : g.personnel_nom.split(' ')[0]} ({g.completed_qty}/{g.total_qty})</span>
                              {(user.role === 'superadmin' || user.role === 'gerant') && g.active_affs.length > 0 && (
                                <span className="flex items-center gap-0.5 ml-1">
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setAssignmentToEdit(g.active_affs[0]); }}
                                    className="w-3.5 h-3.5 flex items-center justify-center rounded bg-white text-gray-600 border border-gray-100 cursor-pointer"
                                    title="Modifier"
                                  >
                                    <i className="ri-pencil-line text-[9px]" />
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.active_affs); }}
                                    className="w-3.5 h-3.5 flex items-center justify-center rounded bg-white text-red-500 border border-red-100 cursor-pointer"
                                    title="Retirer"
                                  >
                                    <i className="ri-close-line text-[9px]" />
                                  </button>
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {(!(cmd as any).affectations || (cmd as any).affectations.filter((a: any) => a.type === activeTab).length === 0) && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${activeTab === 'COUPE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                          {(activeTab === 'COUPE' ? cmd.coupeur : cmd.tailleur)?.charAt(0) || '?'}
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 capitalize">
                          {(activeTab === 'COUPE' ? cmd.coupeur : cmd.tailleur) || 'Non assigné'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                   {/* Mobile action button? Let's use the row's existing actions if needed */}
                   {(user.role === 'superadmin' || user.role === 'gerant') && cmd.statut !== 'EN_ATTENTE' && (
                    <button 
                      onClick={() => setShowUndoModal({id: String(cmd.id), code: String(cmd.id)})}
                      className="flex-1 py-2.5 rounded-xl bg-gray-50 text-red-500 font-bold text-[10px] uppercase tracking-widest border border-red-50"
                    >
                      <i className="ri-arrow-go-back-line mr-1" /> Annuler étape
                    </button>
                  )}
                  <button 
                     onClick={() => navigate(`/commande/${cmd.id}`)}
                     className="flex-1 py-2.5 rounded-xl bg-gray-900 text-white font-bold text-[10px] uppercase tracking-widest"
                  >
                     <i className="ri-eye-line mr-1" /> Détails
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop view: Table */}
          <div className="hidden md:block bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-4 w-12">
                    <input 
                      type="checkbox" 
                      aria-label="Sélectionner tout"
                      className="w-4 h-4 rounded border-gray-300 text-foret-600 focus:ring-foret-500 cursor-pointer"
                      checked={selectedIds.length === searchedCommandes.length && searchedCommandes.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Commande</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Client & Modèle</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Statut</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest">Assigné à</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {searchedCommandes.map(cmd => (
                  <tr key={cmd.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.includes(Number(cmd.id)) ? 'bg-foret-50/30' : ''}`}>
                    <td className="px-6 py-4">
                      <input 
                        type="checkbox" 
                        aria-label={`Sélectionner commande ${cmd.id}`}
                        className="w-4 h-4 rounded border-gray-300 text-foret-600 focus:ring-foret-500 cursor-pointer"
                        checked={selectedIds.includes(Number(cmd.id))}
                        onChange={() => toggleSelect(Number(cmd.id))}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-sm text-gray-900">#{cmd.id}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                          <img src={api.getProxyUrl(cmd.modelImage)} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{cmd.client_nom}</p>
                          <p className="text-xs text-gray-400">{cmd.modele || 'Sur mesure'}</p>
                          {renderProgressBlock(cmd, activeTab)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${statutColors[cmd.statut]}`}>
                        {statutLabels[cmd.statut]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Primary Assignee (Only if no granular affs) */}
                        {(!(cmd as any).affectations || (cmd as any).affectations.filter((a: any) => a.type === activeTab).length === 0) && (
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${activeTab === 'COUPE' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {(activeTab === 'COUPE' ? cmd.coupeur : cmd.tailleur)?.charAt(0) || '?'}
                            </div>
                            <span className="text-sm font-bold text-gray-700">
                              {(activeTab === 'COUPE' ? cmd.coupeur : cmd.tailleur) || 'Non assigné'}
                            </span>
                          </div>
                        )}
                        
                        {(cmd as any).affectations && (cmd as any).affectations.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {getGroupedAffectations((cmd as any).affectations, activeTab).map((g: any) => {
                              const isFullyCompleted = g.completed_qty === g.total_qty;
                              return (
                                <div key={g.personnel_id} className="flex flex-col">
                                   <span 
                                     className={`text-[8px] font-black px-2 py-0.5 rounded-lg border shadow-sm flex items-center gap-1 group/badge ${
                                       isFullyCompleted 
                                         ? 'bg-green-50 text-green-700 border-green-200' 
                                         : 'bg-blue-50 text-blue-700 border-blue-200'
                                     }`}
                                     title={`${g.personnel_nom} : ${g.completed_qty} / ${g.total_qty} terminés`}
                                   >
                                     <span>{g.personnel_nom === 'Inconnu' ? `ID:${g.personnel_id}` : g.personnel_nom.split(' ')[0]} ({g.completed_qty}/{g.total_qty})</span>
                                     {(user.role === 'superadmin' || user.role === 'gerant') && g.active_affs.length > 0 && (
                                       <span className="flex items-center gap-0.5 ml-1 opacity-60 md:opacity-0 md:group-hover/badge:opacity-100 hover:!opacity-100 transition-opacity">
                                         <button 
                                           onClick={(e) => { e.stopPropagation(); setAssignmentToEdit(g.active_affs[0]); }}
                                           className="w-3.5 h-3.5 flex items-center justify-center rounded bg-white hover:bg-gray-100 text-gray-600 border border-gray-100 cursor-pointer"
                                           title="Modifier l'assignation"
                                         >
                                           <i className="ri-pencil-line text-[9px]" />
                                         </button>
                                         <button 
                                           onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.active_affs); }}
                                           className="w-3.5 h-3.5 flex items-center justify-center rounded bg-white hover:bg-red-50 text-red-500 border border-red-100 cursor-pointer"
                                           title="Retirer l'assignation"
                                         >
                                           <i className="ri-close-line text-[9px]" />
                                         </button>
                                       </span>
                                     )}
                                   </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {(user.role === 'superadmin' || user.role === 'gerant') && cmd.statut !== 'EN_ATTENTE' && (
                        <button 
                          onClick={() => setShowUndoModal({id: String(cmd.id), code: String(cmd.id)})}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all cursor-pointer"
                          title="Annuler la dernière étape"
                        >
                          <i className="ri-arrow-go-back-line text-lg" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {searchedCommandes.length === 0 && (
            <div className="py-20 text-center">
              <i className="ri-inbox-line text-4xl text-gray-200 block mb-3" />
              <p className="text-gray-400 font-medium">Aucune commande en attente pour cet atelier</p>
            </div>
          )}
        </div>

        {/* Floating Action Bar */}
        {selectedIds.length > 0 && (
           <div className="fixed bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-6 bg-gray-900 text-white px-8 py-5 rounded-3xl shadow-2xl z-40 border border-white/10 animate-in fade-in slide-in-from-bottom-5">
              <div className="flex items-center gap-3 pr-6 border-r border-white/10">
                <div className="w-8 h-8 rounded-full bg-foret-500 flex items-center justify-center font-bold text-sm">
                  {selectedIds.length}
                </div>
                <span className="text-sm font-bold text-gray-300">sélectionnées</span>
              </div>
              
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setShowAssignModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 rounded-xl text-sm font-bold hover:bg-gray-100 transition-all cursor-pointer"
                >
                  <i className="ri-user-add-line" />
                  Assigner au personnel
                </button>
                <button 
                  onClick={() => setShowValidModal(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-foret-500 text-white rounded-xl text-sm font-bold hover:bg-foret-600 transition-all cursor-pointer shadow-lg shadow-foret-500/20"
                >
                  <i className="ri-check-double-line" />
                  Valider l'étape (Batch)
                </button>
                <button 
                  onClick={() => setSelectedIds([])}
                  aria-label="Fermer la barre d'actions"
                  className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
           </div>
        )}

        {(showAssignModal || assignmentToEdit) && (
          <AssignmentModal 
            commandeIds={assignmentToEdit ? [Number(assignmentToEdit.commande_id)] : selectedIds}
            type={assignmentToEdit ? assignmentToEdit.type : activeTab}
            modeleTypeId={assignmentToEdit 
              ? (commandes.find(c => Number(c.id) === Number(assignmentToEdit.commande_id))?.modele_id) 
              : (selectedIds.length === 1 ? commandes.find(c => Number(c.id) === Number(selectedIds[0]))?.modele_id : undefined)
            }
            assignmentToEdit={assignmentToEdit}
            onClose={() => {
              setShowAssignModal(false);
              setAssignmentToEdit(null);
            }}
            onSuccess={() => {
              showToast(assignmentToEdit ? "Assignation modifiée avec succès !" : `${selectedIds.length} commandes mises à jour`);
              setSelectedIds([]);
              setShowAssignModal(false);
              setAssignmentToEdit(null);
              const token = localStorage.getItem('atelier_bassoum_token');
              if (token) fetchData(token);
            }}
          />
        )}

        {showValidModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowValidModal(false)}>
            <div className="bg-white rounded-3xl p-8 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="font-playfair font-bold text-2xl text-gray-900 mb-1">Validation groupée</h3>
              <p className="text-sm text-gray-500 mb-4">Passer les {selectedIds.length} commandes à l'étape suivante.</p>
              
              {/* Liste récapitulative des affectations par commande */}
              <div className="space-y-4 max-h-[300px] overflow-y-auto mb-6 p-3 bg-gray-50 rounded-2xl border border-gray-100 custom-scrollbar">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest pl-1 mb-2">Détails des tâches à valider ({activeTab})</p>
                {selectedIds.map(cmdId => {
                  const cmd = commandes.find(c => Number(c.id) === cmdId);
                  if (!cmd) return null;
                  const cmdAffs = ((cmd as any).affectations || []).filter((a: any) => a.type === activeTab);
                  
                  if (cmdAffs.length === 0) {
                    return (
                      <div key={cmdId} className="p-3 bg-white rounded-xl border border-gray-100">
                        <p className="text-xs font-black text-gray-900">Commande #{cmdId} — {cmd.client_nom}</p>
                        <p className="text-[10px] text-amber-600 font-bold mt-1.5 flex items-center gap-1">
                          <i className="ri-alert-line text-xs" /> Aucun personnel assigné pour cet atelier.
                        </p>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={cmdId} className="p-4 bg-white rounded-2xl border border-gray-100 space-y-3 shadow-sm">
                      <p className="text-xs font-black text-gray-900 flex items-center gap-2">
                        <i className="ri-folder-open-line text-foret-600" />
                        Commande #{cmdId} — {cmd.client_nom}
                      </p>
                      <div className="space-y-2.5">
                        {cmdAffs.map((aff: any) => {
                          const isCompleted = aff.statut === 'TERMINE';
                          const isChecked = selectedAffIds.includes(aff.id) || isCompleted;
                          
                          return (
                            <label key={aff.id} className={`flex items-start gap-3 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                              isChecked 
                                ? 'bg-foret-50/20 border-foret-200' 
                                : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                            }`}>
                              <input 
                                type="checkbox"
                                className="w-5 h-5 rounded border-gray-300 text-foret-600 focus:ring-foret-500 mt-0.5 cursor-pointer"
                                checked={isChecked}
                                disabled={isCompleted}
                                onChange={() => {
                                  if (isCompleted) return;
                                  setSelectedAffIds(prev => prev.includes(aff.id) ? prev.filter(id => id !== aff.id) : [...prev, aff.id]);
                                }}
                              />
                              <div className="flex-1 min-w-0 flex flex-col gap-1">
                                <div className="flex items-center justify-between gap-1.5 flex-wrap">
                                  <span className="text-xs font-black text-gray-800">
                                    <span className="text-gray-400 font-bold">Exécutant :</span> {aff.personnel_nom}
                                  </span>
                                  {isCompleted ? (
                                    <span className="text-[8px] bg-green-100 text-green-700 px-1.5 py-0.2 rounded-md font-black border border-green-200 uppercase tracking-wider">Terminé</span>
                                  ) : isChecked ? (
                                    <span className="text-[8px] bg-foret-100 text-foret-700 px-1.5 py-0.2 rounded-md font-black border border-foret-200 uppercase tracking-wider">À valider</span>
                                  ) : (
                                    <span className="text-[8px] bg-amber-50 text-amber-700 px-1.5 py-0.2 rounded-md font-black border border-amber-200 uppercase tracking-wider">En attente</span>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1 mt-1 text-[11px]">
                                  <p className="text-gray-600">
                                    <span className="text-gray-400 font-bold">Tâche :</span> <span className="font-semibold text-gray-700">{aff.tache}</span>
                                  </p>
                                  <p className="text-gray-600">
                                    <span className="text-gray-400 font-bold">Couture :</span> <span className="font-bold text-gray-700">{getArticleNomHelper(aff, cmd)}</span>
                                  </p>
                                  <p className="text-gray-600 flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-400 font-bold">Quantité :</span>
                                    {!isCompleted && isChecked && Number(aff.quantite) > 1 ? (
                                      <select
                                        value={completedQuantities[Number(aff.id)] ?? Number(aff.quantite || 1)}
                                        onChange={(e) => {
                                          const val = Number(e.target.value);
                                          setCompletedQuantities(prev => ({ ...prev, [aff.id]: val }));
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="border border-gray-200 rounded-lg px-2 py-0.5 text-xs bg-white font-bold focus:outline-none focus:ring-2 focus:ring-foret-400 cursor-pointer shadow-sm"
                                      >
                                        {Array.from({ length: Number(aff.quantite) }, (_, i) => i + 1).map(val => (
                                          <option key={val} value={val}>{val} / {aff.quantite} pièce{val > 1 ? 's' : ''}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="font-black text-gray-800">{aff.quantite || 1} pièces</span>
                                    )}
                                  </p>
                                  {!isCompleted && isChecked && cmd.articles && cmd.articles.length > 0 ? (
                                    <div className="text-gray-600 flex items-center gap-2 flex-wrap sm:col-span-2 mt-1 pt-1 border-t border-dashed border-gray-100">
                                      <span className="text-gray-400 font-bold">Associer au modèle :</span>
                                      <select
                                        value={completedArticles[Number(aff.id)] ?? aff.article_id ?? ''}
                                        onChange={(e) => {
                                          const val = e.target.value ? Number(e.target.value) : null;
                                          setCompletedArticles(prev => ({ ...prev, [aff.id]: val }));
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="border border-gray-200 rounded-lg px-2 py-1 text-[11px] bg-white font-bold focus:outline-none focus:ring-2 focus:ring-foret-400 cursor-pointer shadow-sm max-w-full"
                                      >
                                        <option value="">Global / Toute la commande</option>
                                        {cmd.articles.map((art: any) => (
                                          <option key={art.id} value={art.id}>
                                            {art.type_habit_nom} {art.modele_nom ? `(${art.modele_nom})` : ''} (Qté: {art.quantite})
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  ) : null}
                                </div>

                                {isChecked && (
                                  <div className="mt-1.5 flex items-center gap-1.5 text-[9px] font-black text-foret-600 bg-white border border-foret-100 px-2 py-0.5 rounded-lg self-start shadow-sm">
                                    <i className="ri-shield-user-line text-xs" />
                                    <span>Validateur : {user?.nom || 'Responsable'}</span>
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-6">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Commentaire global (optionnel)</label>
                <textarea 
                  value={validComment}
                  onChange={e => setValidComment(e.target.value)}
                  placeholder="Note commune pour tout le lot..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-foret-500 focus:border-transparent outline-none transition-all resize-none h-24"
                />
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowValidModal(false)}
                  className="flex-1 py-4 text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-widest"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleBatchValidate}
                  className="flex-[2] py-4 bg-gray-900 text-white rounded-2xl text-sm font-bold hover:bg-black transition-all shadow-xl shadow-gray-200"
                >
                  Confirmer la validation
                </button>
              </div>
            </div>
          </div>
        )}

        {showUndoModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUndoModal(null)}>
            <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
              <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-6 mx-auto">
                <i className="ri-arrow-go-back-line text-3xl" />
              </div>
              <h3 className="font-playfair font-bold text-center text-2xl text-gray-900 mb-3">Annuler l'étape ?</h3>
              <p className="text-sm text-center text-gray-500 mb-8 leading-relaxed">
                Cette action supprimera la dernière validation de la commande #{showUndoModal.code} et reviendra à l'état précédent.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowUndoModal(null)}
                  className="flex-1 py-4 rounded-2xl border border-gray-100 text-gray-400 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors uppercase tracking-widest"
                >
                  Garder
                </button>
                <button 
                  onClick={() => handleRevert(showUndoModal.id)}
                  className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-bold text-sm cursor-pointer hover:bg-red-600 shadow-xl shadow-red-200 transition-all uppercase tracking-widest"
                >
                  Annuler l'étape
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
